import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { isClassOperation } from '@/lib/types/test-cases'
import type {
  ClassOperation,
  ClassOperationItem,
  ClassTestCaseEditorProps,
  EditableClassOperation,
} from './types'
import { formatOperationParts } from './utils'

// =============================================================================
// OperationsPreview - Preview component that aligns return values vertically
// =============================================================================

interface OperationsPreviewProps {
  operations: ClassOperation[]
  testCaseId: string
}

function OperationsPreview({ operations, testCaseId }: OperationsPreviewProps) {
  // Filter out comments and calculate parts for each operation
  const executableOps = operations.filter(isClassOperation)
  const parts = executableOps.map((op) => formatOperationParts(op))

  // Find the maximum call length to align comments
  const maxCallLength = Math.max(...parts.map((p) => p.call.length))

  return (
    <pre className="bg-background/60 rounded-md p-3 font-mono text-xs overflow-x-auto">
      {parts.map((part) => {
        // Create a stable key from the part content
        const partKey = `${testCaseId}-preview-${part.call}-${part.expected}`
        return (
          <div key={partKey} className="text-foreground/90">
            <span>{part.call}</span>
            <span className="text-muted-foreground">
              {' '.repeat(maxCallLength - part.call.length)} #
            </span>
            <span> {part.expected}</span>
          </div>
        )
      })}
    </pre>
  )
}

// =============================================================================
// SortableOperationCard - Individual operation card with @dnd-kit sortable
// =============================================================================

interface SortableOperationCardProps {
  id: string
  operation: ClassOperationItem
  index: number
  onUpdate: (updates: Partial<ClassOperationItem>) => void
  onRemove: () => void
}

function SortableOperationCard({
  id,
  operation,
  index,
  onUpdate,
  onRemove,
}: SortableOperationCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border-border/60 bg-muted/50 rounded-lg border px-3 py-2 transition-all ${
        isDragging ? 'shadow-lg scale-[0.98]' : ''
      }`}
    >
      <div className="flex items-center gap-2">
        {/* Drag handle */}
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground cursor-grab touch-none active:cursor-grabbing shrink-0"
          aria-label="Drag to reorder"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>

        {/* Operation number */}
        <span className="text-muted-foreground text-xs font-medium shrink-0 w-6 text-right">
          {index + 1}.
        </span>

        {/* Method input */}
        <input
          type="text"
          value={operation.method}
          onChange={(e) => onUpdate({ method: e.target.value })}
          placeholder="method"
          className="focus-visible:ring-ring h-7 w-28 shrink-0 rounded-md border border-transparent bg-[#F2F3F4] px-2 py-1 font-mono text-xs text-black focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none dark:border-white/10 dark:bg-[#3C3C3C] dark:text-white"
          spellCheck={false}
          aria-label={`Operation ${index + 1} method`}
        />

        {/* Args input */}
        <input
          type="text"
          value={operation.args}
          onChange={(e) => onUpdate({ args: e.target.value })}
          placeholder="[args]"
          className="focus-visible:ring-ring h-7 w-40 shrink-0 rounded-md border border-transparent bg-[#F2F3F4] px-2 py-1 font-mono text-xs text-black focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none dark:border-white/10 dark:bg-[#3C3C3C] dark:text-white"
          spellCheck={false}
          aria-label={`Operation ${index + 1} arguments`}
        />

        {/* Arrow separator */}
        <span className="text-muted-foreground text-xs w-4 shrink-0 text-center">
          →
        </span>

        {/* Expected output input */}
        <input
          type="text"
          value={operation.expected}
          onChange={(e) => onUpdate({ expected: e.target.value })}
          placeholder="expected"
          className="focus-visible:ring-ring h-7 w-24 shrink-0 rounded-md border border-transparent bg-[#F2F3F4] px-2 py-1 font-mono text-xs text-black focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none dark:border-white/10 dark:bg-[#3C3C3C] dark:text-white disabled:opacity-50"
          spellCheck={false}
          aria-label={`Operation ${index + 1} expected`}
          disabled={operation.expectsError}
          title={
            operation.expectsError ? 'Disabled when expecting error' : undefined
          }
        />

        {/* Expects Error checkbox */}
        <label
          className="w-20 flex items-center justify-center shrink-0 cursor-pointer"
          title="Check if this operation should raise an error"
        >
          <input
            type="checkbox"
            checked={operation.expectsError || false}
            onChange={(e) => onUpdate({ expectsError: e.target.checked })}
            className="h-4 w-4 cursor-pointer appearance-none rounded border border-border bg-[#F2F3F4] transition-colors checked:border-primary checked:bg-primary dark:bg-[#3C3C3C] dark:border-white/10 checked:bg-[url('data:image/svg+xml,%3Csvg%20viewBox=%220%200%2016%2016%22%20fill=%22white%22%20xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cpath%20d=%22M12.207%204.793a1%201%200%20010%201.414l-5%205a1%201%200%2001-1.414%200l-2-2a1%201%200%20011.414-1.414L6.5%209.086l4.293-4.293a1%201%200%20011.414%200z%22/%3E%3C/svg%3E')] checked:bg-[length:100%_100%]"
            aria-label={`Operation ${index + 1} throws error`}
          />
        </label>

        {/* Delete button */}
        <button
          type="button"
          onClick={onRemove}
          className="text-muted-foreground hover:text-destructive p-0.5 rounded transition-colors shrink-0"
          aria-label="Remove operation"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}

// =============================================================================
// ClassTestCaseEditor - Main editor component
// =============================================================================

export function ClassTestCaseEditor({
  testCase,
  testConfig,
  onUpdateConstructorArgs,
  onAddOperation,
  onUpdateOperation,
  onRemoveOperation,
  onMoveOperation,
}: ClassTestCaseEditorProps) {
  const hasConstructorParams =
    testConfig.constructorParameters &&
    testConfig.constructorParameters.length > 0

  // Inner tab state: 'operations' or 'preview'
  const [activeTab, setActiveTab] = useState<'operations' | 'preview'>(
    'operations',
  )

  // Configure dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  // Get the filtered operations with their original indices
  const operationsWithIndices = testCase.operations
    .map((op, idx) => ({ op, idx }))
    .filter((item): item is { op: EditableClassOperation; idx: number } =>
      isClassOperation(item.op),
    )

  // Handle drag end - reorder operations. Rows are identified by each
  // operation's stable `op.id`, not its array position, so a drag still
  // targets the right operation after reordering.
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id) return

    const oldIndex = operationsWithIndices.findIndex(
      (item) => item.op.id === active.id,
    )
    const newIndex = operationsWithIndices.findIndex(
      (item) => item.op.id === over.id,
    )

    if (oldIndex === -1 || newIndex === -1) return

    // Map the dragged/target rows back to their positions in the operations
    // array, which is what the index-addressed onMoveOperation callback expects.
    const oldActualIndex = operationsWithIndices[oldIndex].idx
    const newActualIndex = operationsWithIndices[newIndex].idx
    const direction = newActualIndex > oldActualIndex ? 'down' : 'up'
    const steps = Math.abs(newActualIndex - oldActualIndex)

    // Move the item one position at a time until it reaches the target,
    // tracking its index as it shifts after each swap.
    let currentActualIndex = oldActualIndex

    for (let i = 0; i < steps; i++) {
      // Perform the swap in the original array
      onMoveOperation(currentActualIndex, direction)

      // Update the actual index to track where the item is after each swap
      if (direction === 'down') {
        // Moving down: item moves from index N to N+1
        currentActualIndex += 1
      } else {
        // Moving up: item moves from index N to N-1
        currentActualIndex -= 1
      }
    }
  }

  return (
    <div className="space-y-4">
      {/* Constructor args section */}
      {hasConstructorParams && (
        <div className="border-border/60 bg-muted/30 rounded-lg border p-4 space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
              Constructor
            </h3>
            <span className="text-muted-foreground text-xs">
              {testConfig.className || 'Class'}(
              {testConfig.constructorParameters?.map((p) => p.name).join(', ')})
            </span>
          </div>
          <input
            type="text"
            value={testCase.constructorArgs}
            onChange={(e) => onUpdateConstructorArgs(e.target.value)}
            placeholder="[]"
            className="focus-visible:ring-ring h-9 w-full rounded-md border border-transparent bg-[#F2F3F4] px-3 py-2 font-mono text-xs text-black focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none dark:border-white/10 dark:bg-[#3C3C3C] dark:text-white"
            spellCheck={false}
            aria-label="Constructor arguments"
          />
        </div>
      )}

      {/* Operations/Preview section with tabs */}
      <div className="border-border/60 bg-muted/30 rounded-lg border overflow-hidden">
        {/* Tab header */}
        <div className="border-b border-border/60 px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setActiveTab('operations')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                activeTab === 'operations'
                  ? 'bg-muted/50 text-foreground shadow-sm border border-border/60'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Operations
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('preview')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                activeTab === 'preview'
                  ? 'bg-muted/50 text-foreground shadow-sm border border-border/60'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Preview
            </button>
          </div>
          <span className="text-muted-foreground text-xs">
            {testCase.operations.length} operation
            {testCase.operations.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Tab content */}
        <div className="p-4 space-y-3">
          {activeTab === 'operations' ? (
            <>
              {testCase.operations.length === 0 ? (
                <div className="text-muted-foreground text-center py-6 text-sm">
                  <p>No operations defined.</p>
                  <p className="text-xs mt-1">
                    Add operations to test your class methods.
                  </p>
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={operationsWithIndices.map(({ op }) => op.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2">
                      {/* Column headers */}
                      <div className="flex items-center gap-2 px-3 text-muted-foreground text-xs font-medium">
                        <span className="w-4 shrink-0" />
                        <span className="w-6 shrink-0" />
                        <span className="w-28 shrink-0">Method</span>
                        <span className="w-40 shrink-0">Arguments</span>
                        <span className="w-4 shrink-0" />
                        <span className="w-24 shrink-0">Return</span>
                        <span className="w-20 shrink-0 text-center">
                          Throws Error?
                        </span>
                      </div>
                      {operationsWithIndices.map(({ op, idx }) => (
                        <SortableOperationCard
                          key={op.id}
                          id={op.id}
                          operation={op}
                          index={idx}
                          onUpdate={(updates) =>
                            onUpdateOperation(idx, updates)
                          }
                          onRemove={() => onRemoveOperation(idx)}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}

              {/* Add operation button */}
              <button
                type="button"
                onClick={onAddOperation}
                className="border-border text-muted-foreground hover:border-primary hover:text-foreground focus-visible:ring-ring w-full inline-flex items-center justify-center gap-1 rounded-md border border-dashed px-3 py-2 text-xs font-medium transition focus-visible:ring-2 focus-visible:outline-none"
              >
                <Plus className="h-4 w-4" />
                Add Operation
              </button>
            </>
          ) : (
            /* Preview tab */
            <div className="space-y-1">
              {testCase.operations.length === 0 ? (
                <div className="text-muted-foreground text-center py-6 text-sm">
                  <p>No operations to preview.</p>
                  <p className="text-xs mt-1">
                    Switch to Operations tab to add some.
                  </p>
                </div>
              ) : (
                <OperationsPreview
                  operations={testCase.operations}
                  testCaseId={testCase.id}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
